import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  FolderIcon,
  MegaphoneIcon,
  BookOpenIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Pagination } from '@/components/ui'
import { forumApi } from '@/api'
import type { ForumCategory, ThreadListItem } from '@/api/forum'

// Map icon names to Heroicons components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'megaphone': MegaphoneIcon,
  'message-circle': ChatBubbleLeftRightIcon,
  'book-open': BookOpenIcon,
  'lightbulb': LightBulbIcon,
  'help-circle': QuestionMarkCircleIcon,
  'users': UserGroupIcon,
}

const getCategoryIcon = (iconName: string | undefined) => {
  if (!iconName) return FolderIcon
  return iconMap[iconName] || FolderIcon
}

export default function ForumDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [category, setCategory] = useState<ForumCategory | null>(null)
  const [threads, setThreads] = useState<ThreadListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    if (slug) {
      fetchCategoryData()
    }
  }, [slug, currentPage])

  const fetchCategoryData = async () => {
    setLoading(true)
    try {
      const [categoryRes, threadsRes] = await Promise.allSettled([
        forumApi.getCategoryBySlug(slug!),
        forumApi.getThreads({
          category: slug,
          page: currentPage,
          ordering: '-is_pinned,-created_at',
        }),
      ])

      if (categoryRes.status === 'fulfilled') {
        setCategory(categoryRes.value)
      }

      if (threadsRes.status === 'fulfilled') {
        setThreads(threadsRes.value.results)
        setTotalPages(Math.ceil(threadsRes.value.count / 20))
        setTotalItems(threadsRes.value.count)
      }
    } catch (error) {
      console.error('Error fetching category data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento categoria..." />
  }

  if (!category) {
    return (
      <EmptyState
        icon={<FolderIcon className="h-12 w-12" />}
        title="Categoria non trovata"
        description="La categoria richiesta non esiste"
        action={{
          label: 'Torna al Forum',
          onClick: () => window.location.href = '/forum',
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/forum" className="hover:text-primary-600">
          Forum
        </Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900">{category.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {(() => {
            const IconComponent = getCategoryIcon(category.icon)
            return (
              <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-primary-100">
                <IconComponent className="h-8 w-8 text-primary-600" />
              </div>
            )
          })()}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{category.name}</h1>
            {category.description && (
              <p className="text-gray-600">{category.description}</p>
            )}
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span>{category.threads_count} discussioni</span>
              <span>{category.posts_count} messaggi</span>
            </div>
          </div>
        </div>
        <Link
          to={`/forum/new?category=${slug}`}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova discussione
        </Link>
      </div>

      {/* Threads */}
      <Card>
        <CardHeader title="Discussioni" />
        {threads.length > 0 ? (
          <>
            <div className="divide-y divide-gray-100">
              {threads.map((thread) => (
                <Link
                  key={thread.id}
                  to={`/forum/thread/${thread.id}`}
                  className="flex items-start py-4 hover:bg-gray-50 transition-colors px-2 -mx-2 rounded-lg"
                >
                  <Avatar
                    size="md"
                    name={thread.author.display_name}
                    src={thread.author.avatar_url || undefined}
                  />
                  <div className="ml-4 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {thread.is_pinned && (
                        <Badge size="sm" variant="warning">Fissato</Badge>
                      )}
                      {thread.is_locked && (
                        <Badge size="sm" variant="danger">Chiuso</Badge>
                      )}
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {thread.title}
                      </h3>
                    </div>
                    {/* Content not available in list view */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span>{thread.author.display_name}</span>
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {formatDate(thread.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-sm text-gray-500 ml-4">
                    <div className="flex items-center">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {thread.views_count}
                    </div>
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                      {thread.posts_count}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <EmptyState
            icon={<ChatBubbleLeftRightIcon className="h-12 w-12" />}
            title="Nessuna discussione"
            description="Sii il primo a iniziare una discussione in questa categoria!"
            action={{
              label: 'Nuova discussione',
              onClick: () => window.location.href = `/forum/new?category=${slug}`,
            }}
          />
        )}
      </Card>
    </div>
  )
}
